import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateWithOrdinalPipe } from './date-with-ordinal.pipe';

/**
 * Shared component for desktop table row selector and refresh button
 */
@Component({
  selector: 'app-desktop-status-row',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="desktop-status-row-wrapper">
      <div class="rows-selector-container">
        <button type="button" class="rows-selector-btn" (click)="toggleRowsDropdown.emit($event)">
          <span class="rows-text-group">
            <span class="rows-label">Rows </span>
            <span class="rows-value">{{ itemsPerPage }}</span>
          </span>
          <img src="/assets/images/Chevron_Left.png" class="rows-chevron" alt="expand">
        </button>
        
        @if (rowsDropdownOpen) {
          <div class="rows-dropdown-menu">
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(10); $event.stopPropagation()">10</button>
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(25); $event.stopPropagation()">25</button>
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(50); $event.stopPropagation()">50</button>
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(100); $event.stopPropagation()">100</button>
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(150); $event.stopPropagation()">150</button>
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(200); $event.stopPropagation()">200</button>
            <button type="button" class="rows-option" (click)="setItemsPerPage.emit(250); $event.stopPropagation()">250</button>
          </div>
        }
      </div>

      <button type="button" class="desktop-refresh-btn" (click)="refresh.emit()">
        Refresh
      </button>
    </div>
  `,
  styles: [`
    :host {
      /* When used inside a flex row (e.g. Growers/Transloading status row),
         ensure this control group docks to the right like Scheduler. */
      display: block;
      margin-left: auto;
    }

    .desktop-status-row-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .rows-selector-container {
      position: relative;
      margin-right: 4px;
      flex-shrink: 0;
    }

    .rows-selector-btn {
      width: fit-content;
      min-width: 108px;
      max-width: 200px;
      height: 45px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: white;
      border: 2px solid #C3C3C7;
      border-radius: 12px;
      padding: 8px 12px;
      cursor: pointer;
      box-sizing: border-box;
    }

    .rows-text-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .rows-label {
      font-family: Lato, sans-serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 21px;
      color: #505050;
      white-space: nowrap;
    }

    .rows-value {
      font-family: Lato, sans-serif;
      font-weight: 700;
      font-size: 16px;
      line-height: 21px;
      color: #000000;
    }

    .rows-chevron {
      width: 6px;
      height: 12px;
      transform: rotate(270deg);
      vertical-align: middle;
    }

    .rows-selector-btn:active .rows-chevron {
      transform: rotate(270deg) scale(0.9);
    }

    .rows-dropdown-menu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 100%;
      background: white;
      border: 1px solid #C3C3C7;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 1000;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .rows-option {
      padding: 12px 20px;
      font-family: Lato, sans-serif;
      font-size: 18px;
      color: #000000;
      cursor: pointer;
      text-align: left;
      background: white;
      border: none;
    }

    .rows-option:hover {
      background-color: #F1F3F9;
    }

    .desktop-refresh-btn {
      width: 120px;
      height: 45px;
      border-radius: 115px;
      padding: 12px;
      background-color: #FEE000;
      border: none;
      font-family: Inter, sans-serif;
      font-weight: 600;
      font-size: 18px;
      color: #000000;
      cursor: pointer;
      flex-shrink: 0;
    }

    /* Responsive adjustments for intermediate screen widths */
    @media (min-width: 768px) and (max-width: 1100px) {
      .desktop-status-row-wrapper {
        gap: 8px;
      }

      .rows-selector-btn {
        width: fit-content;
        min-width: 98px;
        max-width: 160px;
        height: 45px;
        padding: 8px 10px;
      }

      .rows-label {
        font-size: 14px;
      }

      .rows-value {
        font-size: 14px;
      }

      .desktop-refresh-btn {
        width: 90px;
        height: 40px;
        font-size: 14px;
        padding: 8px;
      }
    }

    /* Even smaller screens */
    @media (min-width: 768px) and (max-width: 900px) {
      .desktop-status-row-wrapper {
        gap: 4px;
      }

      .rows-selector-btn {
        width: fit-content;
        min-width: 88px;
        max-width: 130px;
        height: 40px;
        padding: 6px 8px;
        border-radius: 12px;
      }

      .rows-label {
        font-size: 12px;
      }

      .rows-value {
        font-size: 12px;
      }

      .desktop-refresh-btn {
        width: 75px;
        height: 36px;
        font-size: 12px;
        padding: 6px;
      }
    }
  `]
})
export class DesktopStatusRowComponent {
  @Input() itemsPerPage: number = 10;
  @Input() rowsDropdownOpen: boolean = false;
  @Output() toggleRowsDropdown = new EventEmitter<Event>();
  @Output() setItemsPerPage = new EventEmitter<number>();
  @Output() refresh = new EventEmitter<void>();
}

/**
 * Shared component for formatted date display
 */
@Component({
  selector: 'app-formatted-date',
  standalone: true,
  imports: [DateWithOrdinalPipe],
  template: `
    <div style="display: flex; flex-direction: column; line-height: 1.2;">
      <span>{{ ((dueDateTime | dateWithOrdinal:'EEE, d MMM y, HH:mm') || '-').split(', ').slice(0, 2).join(', ') }}</span>
      <span>{{ ((dueDateTime | dateWithOrdinal:'EEE, d MMM y, HH:mm') || '-').split(', ').slice(2).join(', ') }}</span>
    </div>
  `
})
export class FormattedDateComponent {
  @Input() dueDateTime: string | null | undefined;
}

/**
 * Shared component for action buttons (history and comments)
 */
@Component({
  selector: 'app-action-buttons',
  standalone: true,
  imports: [CommonModule],
  template: `
    <td class="col-actions">
      <div class="action-buttons-wrapper">
        <button type="button" class="action-btn" (click)="history.emit(recordId)" aria-label="View history">
          <img src="/assets/images/History_Button.png" alt="history" class="action-icon history-icon">
        </button>
        <button type="button" class="action-btn" (click)="comments.emit(recordId)" aria-label="View comments">
          <img src="/assets/images/Comments_Icon_Black.png" alt="comments" class="action-icon comments-icon">
        </button>
      </div>
    </td>
  `,
  styles: [`
    :host {
      /* Ensure the <td> participates in the table layout like a direct <tr> child */
      display: contents;
    }

    .col-actions {
      text-align: center;
      vertical-align: middle;
      white-space: nowrap;
      padding: 16px;
    }

    :host-context(.desktop-passports-table tr.has-pcr-changes) .col-actions {
      background: #FFFCE5 !important;
    }

    :host-context(.desktop-passports-table tr.is-cancelled) .col-actions {
      background: #FCEAE5 !important;
    }

    .action-buttons-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }

    .action-btn {
      background: #FEE000;
      border: none;
      width: 28px;
      height: 28px;
      border-radius: 64px;
      opacity: 1;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      position: relative;
      flex: none;
      flex-grow: 0;
      overflow: visible;
    }

    .action-icon {
      object-fit: contain;
      width: 18px;
      height: 18px;
    }
  `]
})
export class ActionButtonsComponent {
  @Input() recordId: string = '';
  @Output() history = new EventEmitter<string>();
  @Output() comments = new EventEmitter<string>();
}

/**
 * Shared component for pagination controls
 */
@Component({
  selector: 'app-desktop-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="desktop-pagination">
      <div class="pagination-info">
        <span class="page-range-start">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
        <span class="page-range-end"> - {{ Math.min(currentPage * itemsPerPage, visibleRecordsLength) }} of {{ visibleRecordsLength }} Passports</span>
      </div>
      <div class="pagination-controls">
        <span class="page-label">The page on</span>
        <div class="page-select-wrapper">
          <select [ngModel]="currentPage" (ngModelChange)="pageChange.emit(+$event)">
            @for (p of [].constructor(totalPages); track $index) {
              <option [value]="$index + 1">{{ $index + 1 }}</option>
            }
          </select>
        </div>
        <button class="page-nav-btn prev" [disabled]="currentPage === 1" (click)="pageChange.emit(currentPage - 1)">
          <img src="/assets/images/Chevron_Left.png" alt="prev">
        </button>
        <button class="page-nav-btn next" [disabled]="currentPage === totalPages" (click)="pageChange.emit(currentPage + 1)">
          <img src="/assets/images/Chevron_Left.png" alt="next" style="transform: rotate(180deg)">
        </button>
      </div>
    </div>
  `,
  styles: [`
    .desktop-pagination {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-family: Lato, sans-serif;
      font-size: 14px;
    }

    .pagination-info {
      display: flex;
      align-items: center;
    }

    .page-range-start {
      font-weight: 700;
      color: #1A71F6;
    }

    .page-range-end {
      color: #737373;
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .page-label {
      font-family: Lato, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 150%;
      color: #454545;
      vertical-align: middle;
    }

    .page-select-wrapper {
      position: relative;
      display: inline-block;
    }

    .page-select-wrapper select {
      width: 45px;
      height: 29px;
      border-radius: 8px;
      padding: 4px 6px 4px 8px;
      border: 1px solid #B0B0B0;
      font-family: Lato, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 150%;
      color: #454545;
      vertical-align: middle;
      background: white;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
    }

    .page-select-wrapper::after {
      content: '';
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 6px solid #454545;
      pointer-events: none;
    }

    .page-nav-btn {
      width: 32px;
      height: 28px;
      border-radius: 8px;
      padding: 4px 6px;
      border: 1px solid #B0B0B0;
      background: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .page-nav-btn img {
      width: 7px;
      height: 14px;
    }

    .page-nav-btn:disabled {
      opacity: 0.3;
      cursor: default;
    }
  `]
})
export class DesktopPaginationComponent {
  @Input() currentPage: number = 1;
  @Input() itemsPerPage: number = 10;
  @Input() totalPages: number = 1;
  @Input() visibleRecordsLength: number = 0;
  @Output() pageChange = new EventEmitter<number>();
  
  get Math() { return Math; }
}

/**
 * Shared component for desktop tab status row with tabs, rows selector, and refresh button
 */
@Component({
  selector: 'app-desktop-tab-status-row',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="desktop-status-row">
      <div class="status-tabs-group">
        <button type="button" class="desktop-status-btn" 
                [class.selected]="selectedTab === 'all'"
                (click)="tabSelect.emit('all')">
          All ({{ allCount }})
        </button>
        <button type="button" class="desktop-status-btn" 
                [class.selected]="selectedTab === 'load'"
                (click)="tabSelect.emit('load')">
          Load ({{ loadCount }})
        </button>
        <button type="button" class="desktop-status-btn" 
                [class.selected]="selectedTab === 'shunting'"
                (click)="tabSelect.emit('shunting')">
          Shunting ({{ shuntingCount }})
        </button>
        <button type="button" class="desktop-status-btn" 
                [class.selected]="selectedTab === 'transloading'"
                (click)="tabSelect.emit('transloading')">
          Transloading ({{ transloadingCount }})
        </button>
        <button type="button" class="desktop-status-btn" 
                [class.selected]="selectedTab === 'receiving'"
                (click)="tabSelect.emit('receiving')">
          Receiving ({{ receivingCount }})
        </button>
        <button type="button" class="desktop-status-btn" 
                [class.selected]="selectedTab === 'transferred'"
                (click)="tabSelect.emit('transferred')">
          Transferred ({{ transferredCount }})
        </button>
      </div>

      <div class="rows-selector-container">
        <button type="button" class="rows-selector-btn" (click)="toggleRowsDropdown.emit($event)">
          <span class="rows-text-group">
            <span class="rows-label">Rows </span>
            <span class="rows-value">{{ itemsPerPage }}</span>
          </span>
          <img src="/assets/images/Chevron_Left.png" class="rows-chevron" alt="expand">
        </button>
        
        <div *ngIf="rowsDropdownOpen" class="rows-dropdown-menu">
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(10); $event.stopPropagation()">10</button>
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(25); $event.stopPropagation()">25</button>
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(50); $event.stopPropagation()">50</button>
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(100); $event.stopPropagation()">100</button>
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(150); $event.stopPropagation()">150</button>
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(200); $event.stopPropagation()">200</button>
          <button type="button" class="rows-option" (click)="setItemsPerPage.emit(250); $event.stopPropagation()">250</button>
        </div>
      </div>

      <button type="button" class="desktop-refresh-btn" (click)="refresh.emit()">
        Refresh
      </button>
    </div>
  `,
  styles: [`
    /*
     * Default = laptop-friendly (formerly only up to 1100px viewport).
     * Large monitors: @media (min-width: 1601px).
     * Narrow desktop height: @media (768px–900px).
     * Narrow card (ancestor container): @container passports-desktop-toolbar.
     */
    .desktop-status-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      flex-wrap: nowrap;
      max-width: 100%;
    }

    .status-tabs-group {
      display: flex;
      align-items: center;
      gap: 4px;
      flex: 1 1 auto;
      flex-shrink: 1;
      min-width: 0;
      overflow-x: auto;
      scrollbar-width: thin;
      scrollbar-color: #c3c3c7 #f1f3f9;
    }

    .status-tabs-group::-webkit-scrollbar {
      height: 4px;
    }

    .status-tabs-group::-webkit-scrollbar-thumb {
      background: #c3c3c7;
      border-radius: 4px;
    }

    .desktop-status-btn {
      height: 40px;
      border-radius: 10px;
      padding: 8px 6px;
      font-family: Lato, sans-serif;
      font-size: clamp(12px, 0.65vw + 10px, 14px);
      font-weight: 500;
      text-align: center;
      color: #828288;
      background: transparent;
      border: 2px solid #C3C3C7;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .desktop-status-btn.selected {
      background-color: #4A6FCC;
      border-color: #4A6FCC;
      color: #FFFFFF;
      font-weight: 700;
      font-size: clamp(12px, 0.65vw + 10px, 14px);
    }

    .rows-selector-container {
      margin-left: auto;
      position: relative;
      margin-right: 4px;
      flex-shrink: 0;
    }

    .rows-selector-btn {
      width: fit-content;
      min-width: 98px;
      max-width: 160px;
      height: 45px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: white;
      border: 2px solid #C3C3C7;
      border-radius: 12px;
      padding: 8px 10px;
      cursor: pointer;
      box-sizing: border-box;
    }

    .rows-text-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .rows-label {
      font-family: Lato, sans-serif;
      font-weight: 400;
      font-size: 14px;
      line-height: 21px;
      color: #505050;
      white-space: nowrap;
    }

    .rows-value {
      font-family: Lato, sans-serif;
      font-weight: 700;
      font-size: 14px;
      line-height: 21px;
      color: #000000;
    }

    .rows-chevron {
      width: 6px;
      height: 12px;
      transform: rotate(270deg);
      vertical-align: middle;
    }

    .rows-selector-btn:active .rows-chevron {
      transform: rotate(270deg) scale(0.9);
    }

    .rows-dropdown-menu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 100%;
      background: white;
      border: 1px solid #C3C3C7;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 1000;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .rows-option {
      padding: 12px 20px;
      font-family: Lato, sans-serif;
      font-size: 18px;
      color: #000000;
      cursor: pointer;
      text-align: left;
      background: white;
      border: none;
    }

    .rows-option:hover {
      background-color: #F1F3F9;
    }

    .desktop-refresh-btn {
      width: 90px;
      height: 40px;
      border-radius: 115px;
      padding: 8px;
      background-color: #FEE000;
      border: none;
      font-family: Inter, sans-serif;
      font-weight: 600;
      font-size: 14px;
      color: #000000;
      cursor: pointer;
      flex-shrink: 0;
    }

    /* Narrow desktop viewport */
    @media (min-width: 768px) and (max-width: 900px) {
      .desktop-status-row {
        gap: 4px;
      }

      .status-tabs-group {
        gap: 4px;
      }

      .desktop-status-btn {
        padding: 6px 6px;
        font-size: 12px;
        height: 36px;
        border-radius: 8px;
      }

      .desktop-status-btn.selected {
        font-size: 12px;
      }

      .rows-selector-btn {
        width: fit-content;
        min-width: 88px;
        max-width: 130px;
        height: 40px;
        padding: 6px 8px;
        border-radius: 12px;
      }

      .rows-label {
        font-size: 12px;
      }

      .rows-value {
        font-size: 12px;
      }

      .desktop-refresh-btn {
        width: 75px;
        height: 36px;
        font-size: 12px;
        padding: 6px;
      }
    }

    /* Very large monitors — roomier controls */
    @media (min-width: 1601px) {
      .desktop-status-row {
        gap: 10px;
      }

      .status-tabs-group {
        gap: 6px;
      }

      .desktop-status-btn {
        height: 45px;
        padding: 10px 8px;
        font-size: 14px;
      }

      .desktop-status-btn.selected {
        font-size: 14px;
      }

      .rows-selector-btn {
        min-width: 108px;
        max-width: 200px;
        height: 45px;
        padding: 8px 12px;
      }

      .rows-label {
        font-size: 16px;
      }

      .rows-value {
        font-size: 16px;
      }

      .desktop-refresh-btn {
        width: 120px;
        height: 45px;
        font-size: 18px;
        padding: 12px;
      }
    }

    /*
     * When the white card is narrower than the viewport (e.g. wide window but constrained layout),
     * override the large-monitor tier so tabs stay usable.
     */
    @container passports-desktop-toolbar (max-width: 1200px) {
      .desktop-status-row {
        gap: 8px;
      }

      .status-tabs-group {
        gap: 4px;
      }

      .desktop-status-btn {
        height: 40px;
        padding: 8px 6px;
        font-size: clamp(12px, 0.65vw + 10px, 14px);
      }

      .desktop-status-btn.selected {
        font-size: clamp(12px, 0.65vw + 10px, 14px);
      }

      .rows-selector-btn {
        min-width: 98px;
        max-width: 160px;
        height: 45px;
        padding: 8px 10px;
      }

      .rows-label {
        font-size: 14px;
      }

      .rows-value {
        font-size: 14px;
      }

      .desktop-refresh-btn {
        width: 90px;
        height: 40px;
        font-size: 14px;
        padding: 8px;
      }
    }

    @container passports-desktop-toolbar (max-width: 900px) {
      .desktop-status-row {
        gap: 4px;
      }

      .status-tabs-group {
        gap: 4px;
      }

      .desktop-status-btn {
        padding: 6px 6px;
        font-size: 12px;
        height: 36px;
        border-radius: 8px;
      }

      .desktop-status-btn.selected {
        font-size: 12px;
      }

      .rows-selector-btn {
        min-width: 88px;
        max-width: 130px;
        height: 40px;
        padding: 6px 8px;
        border-radius: 12px;
      }

      .rows-label {
        font-size: 12px;
      }

      .rows-value {
        font-size: 12px;
      }

      .desktop-refresh-btn {
        width: 75px;
        height: 36px;
        font-size: 12px;
        padding: 6px;
      }
    }
  `]
})
export class DesktopTabStatusRowComponent {
  @Input() selectedTab: string = 'all';
  @Input() allCount: number = 0;
  @Input() loadCount: number = 0;
  @Input() shuntingCount: number = 0;
  @Input() transloadingCount: number = 0;
  @Input() receivingCount: number = 0;
  @Input() transferredCount: number = 0;
  @Input() itemsPerPage: number = 10;
  @Input() rowsDropdownOpen: boolean = false;
  @Output() tabSelect = new EventEmitter<string>();
  @Output() toggleRowsDropdown = new EventEmitter<Event>();
  @Output() setItemsPerPage = new EventEmitter<number>();
  @Output() refresh = new EventEmitter<void>();
}

